import { useEffect, useMemo, useState } from 'react'
import { apiJson } from '../api'
import { pageStyles } from '../styles'
import type { ThemeTokens } from '../theme'

export interface DirectoryGroup {
  id: string
  displayName?: string
}

export function groupLabel(g: DirectoryGroup): string {
  return g.displayName ?? g.id
}

/**
 * A debounced Entra SECURITY GROUP type-ahead: search `/api/groups`, pick a
 * real group by its object id. Mirrors `DirectoryPicker` (people search)
 * exactly — same debounce, same dropdown styling — but there was no
 * general group search anywhere in the panel before the access-grants
 * matrix (unified-admin-access-control Task 5): the only existing group UI
 * is the fixed CHU-GPT-* capability-group checkboxes (Users.tsx), which
 * can't name an arbitrary Entra group.
 */
export function GroupPicker({
  tokens,
  onPick,
  placeholder = 'Search Entra groups by name…',
  excludeIds = [],
  disabled = false,
}: {
  tokens: ThemeTokens
  onPick: (group: DirectoryGroup) => void
  placeholder?: string
  excludeIds?: string[]
  disabled?: boolean
}) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DirectoryGroup[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const data = await apiJson<{ groups: DirectoryGroup[] }>(`/api/groups?q=${encodeURIComponent(q)}`)
          if (!cancelled) setResults(data.groups)
        } catch {
          if (!cancelled) setResults([])
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  const candidates = results.filter((g) => !excludeIds.includes(g.id))

  return (
    <div style={styles.pickerWrap}>
      <input
        aria-label="Search groups"
        style={{ ...styles.input, minWidth: '20rem' }}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {query.trim().length >= 2 ? (
        <div style={styles.pickerDropdown}>
          {loading ? (
            <div style={styles.pickerOptionMeta}>Searching…</div>
          ) : candidates.length === 0 ? (
            <div style={styles.pickerOptionMeta}>No matching groups.</div>
          ) : (
            candidates.map((g) => (
              <button
                key={g.id}
                type="button"
                style={styles.pickerOption}
                onClick={() => {
                  onPick(g)
                  setQuery('')
                  setResults([])
                }}
              >
                <span>{groupLabel(g)}</span>
                <span style={styles.pickerOptionMeta}>{g.id}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
